import { Link, useParams } from 'react-router-dom';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { useSeo } from '@/hooks/useSeo';

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === Number(id));

  useSeo({
    title: comic ? `${comic.title} - 动漫之家漫画网` : '漫画详情 - 动漫之家漫画网',
    description: comic ? comic.description : '未找到该漫画，返回动漫之家漫画网继续浏览。',
    canonicalPath: comic ? `/comic/${comic.id}` : undefined,
  });

  if (!comic) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-20 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-background-400"></i>
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">未找到该漫画</h1>
        <p className="mt-2 text-sm text-foreground-500">作品可能已下架或链接有误。</p>
        <Link to="/" className="mt-6 inline-block whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const related = comics.filter((c) => c.id !== comic.id && (c.genre === comic.genre || c.tags.some((t) => comic.tags.includes(t)))).slice(0, 5);
  const chapters = Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => comic.chapters - i);

  return (
    <div>
      {/* Hero band */}
      <section className="border-b border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-secondary-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/genre" className="transition-colors hover:text-primary-600">分类大全</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{comic.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
            <div className="mx-auto w-52 shrink-0 lg:mx-0 lg:w-full lg:max-w-[240px]">
              <img src={comic.cover} alt={`${comic.title}封面`} className="aspect-[5/7] w-full rounded-xl object-cover" />
            </div>
            <div className="w-full">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium text-background-50 ${comic.status === '已完结' ? 'bg-accent-500' : 'bg-primary-500'}`}>
                  {comic.status}
                </span>
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-700">{comic.genre}</span>
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-200 px-2.5 py-1 text-xs text-foreground-600">{t}</span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-4xl">{comic.title}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
                <span>作者：{comic.author}</span>
                <span>连载于：{comic.year}</span>
                <span>章节：{comic.chapters}话</span>
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1 text-2xl font-bold text-accent-600">
                  <i className="ri-star-fill text-accent-500"></i>
                  {comic.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-fire-fill text-primary-500"></i> 热度 {(comic.heat / 10000).toFixed(1)}万
                </span>
              </div>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700">{comic.description}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="#chapters" className="whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-book-2-line mr-1"></i> 开始阅读
                </Link>
                <Link to={`/genre?type=${encodeURIComponent(comic.genre)}`} className="whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100">
                  同类作品
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body: chapters + related */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:px-6 lg:grid-cols-[1fr_320px]" aria-label="漫画详情">
        <div>
          <div id="chapters" className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-foreground-950">章节目录</h2>
            <span className="text-sm text-foreground-500">共 {comic.chapters} 话 · 展示最近 {chapters.length} 话</span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {chapters.map((ch) => (
              <a
                key={ch}
                href="#"
                onClick={(e) => e.preventDefault()}
                className="group rounded-lg border border-background-200 bg-background-100 px-3 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-background-50 hover:text-primary-600"
              >
                <span className="font-medium">{comic.title} 第{ch}话</span>
                <span className="mt-1 block text-xs text-foreground-400">第{ch}话</span>
              </a>
            ))}
          </div>
          <p className="mt-4 text-xs text-foreground-400">注：阅读页需前往对应平台，本站仅提供作品信息与导航。</p>
        </div>

        <aside className="rounded-lg border border-background-200 bg-background-100 p-5">
          <h3 className="font-heading text-base text-foreground-950">作品信息</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-foreground-500">作品名称</dt><dd className="text-foreground-900">{comic.title}</dd></div>
            <div className="flex justify-between"><dt className="text-foreground-500">作者</dt><dd className="text-foreground-900">{comic.author}</dd></div>
            <div className="flex justify-between"><dt className="text-foreground-500">类型</dt><dd className="text-foreground-900">{comic.genre}</dd></div>
            <div className="flex justify-between"><dt className="text-foreground-500">状态</dt><dd className="text-foreground-900">{comic.status}</dd></div>
            <div className="flex justify-between"><dt className="text-foreground-500">评分</dt><dd className="flex items-center gap-1 text-accent-600"><i className="ri-star-fill text-sm"></i>{comic.rating.toFixed(1)}</dd></div>
          </dl>
        </aside>
      </section>

      {/* Related */}
      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
            <Link to="/genre" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
              浏览全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Article JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            description: comic.description,
            image: comic.cover,
            author: { '@type': 'Person', name: comic.author },
            genre: comic.genre,
            datePublished: `${comic.year}-01-01`,
            publisher: { '@type': 'Organization', name: '动漫之家漫画网' },
          }),
        }}
      />
    </div>
  );
}